/**
 * Screen effects — camera shake, flashes, vignettes, particle bursts.
 *
 * Carried from v1 with one behavioural change that matters in an 8-player fight:
 *
 * v1's `spawnParticles` evicted the OLDEST burst whenever the budget was full,
 * and the budget was 20 on ultra / 5 on low. Eight players casting abilities
 * blow through that in under a second, so the ultimate you just fired vanished
 * because someone else landed a basic attack. Budgets are ~3x higher now
 * (PostProcessing.js) and eviction prefers a burst that is nearly finished; only
 * if every burst is fresh does it fall back to the oldest.
 */

import * as THREE from 'three/webgpu';
import { PLAYER_HALF_HEIGHT } from '@chaos/sim';
import { cameraShake, particles, frame, cameraState, freeCamPos, remotePlayers, localPlayer } from '../state.js';
import { getParticleBudget } from '../PostProcessing.js';
import {
  createParticleMaterial,
  createShardMaterial,
  setParticleParams,
  setParticleFade,
} from './ParticleUtils.js';

let _scene = null;

export function initScreenEffects(scene) {
  _scene = scene;
}

/**
 * Camera shake, the Brawl Stars way: one amplitude that decays exponentially
 * (rate 9/s), driven through two incommensurate sines rather than white noise,
 * and FALLING OFF WITH DISTANCE from the camera's focus — a slam across the
 * arena is a tremor, the one under your feet is a shove.
 *
 * `at` is the world point of the event; omit it for a shake that is about YOU
 * (taking a hit, dying). The offset is applied to the camera position AND its
 * look target (CameraController), so the whole frame moves rather than the
 * lens swinging about a fixed point.
 */
const SHAKE_DECAY = 9;
/** Beyond this distance from the focus an event shakes nothing. */
const SHAKE_RANGE = 8;

/**
 * Where the camera is looking: the free camera, the body the death cam follows,
 * or your own drawn body. Shake falloff, the light pool's scoring and the
 * decoration budget all measure distance from this one point.
 */
export function cameraFocus() {
  if (cameraState.free) return freeCamPos;
  if (cameraState.followId) {
    const mesh = remotePlayers.get(cameraState.followId);
    if (mesh) return mesh.position;
  }
  return localPlayer.renderPos;
}

/** Distance falloff for a shake at `at`, 1 at the focus and 0 at SHAKE_RANGE. */
export function shakeFalloff(at, focus = cameraFocus()) {
  if (!at) return 1;
  const dx = at.x - focus.x;
  const dz = at.z - focus.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  return Math.max(0, Math.min(1, 1 - d / SHAKE_RANGE));
}

/**
 * Kick the camera. `intensity` falls off with distance from the focus when `at`
 * is given. `duration` is IGNORED: the shake decays at SHAKE_DECAY per second
 * whatever the caller asks. It stays in the signature only because callers on
 * other branches pass it positionally before `at`.
 */
export function triggerCameraShake(intensity, duration = 200, at = null) {
  const amp = intensity * shakeFalloff(at);
  if (amp <= 0) return;
  // Never let a small shake cut a big one short.
  cameraShake.intensity = Math.max(cameraShake.intensity, amp);
  cameraShake.duration = duration;
  cameraShake.startTime = performance.now();
}

export function updateCameraShake(dt = frame.delta) {
  if (cameraShake.intensity <= 0.002) {
    cameraShake.intensity = 0;
    cameraShake.offset.set(0, 0, 0);
  } else {
    cameraShake.intensity *= Math.exp(-SHAKE_DECAY * dt);
    const amp = cameraShake.intensity;
    const t = performance.now() / 1000;
    cameraShake.offset.set(
      Math.sin(t * 43) * amp * 0.3,
      Math.sin(t * 31 + 0.7) * amp * 0.08,
      Math.cos(t * 37 + 1.3) * amp * 0.22
    );
  }
  if (_tremor.mag > 0) updateTremor(dt);
}

// ─── Tremor ─────────────────────────────────────────────────────
//
// TREMOR, the market event: the ground shakes, and the sim applies no force.
// So the whole cue is presentation, and it has to be readable without being
// a nuisance for its whole duration (up to eight seconds): a CONTINUOUS
// low-frequency rumble on the camera scaled by the magnitude, a random jolt
// every second or so, and DECK DUST kicking up around your feet — the ground
// cue, because a shaking camera alone reads as "my hands", not "the floor".
// The arrival is a proper jolt with a ring of dust, so the event lands.

/** Deck dust: a darker tan than the landing puffs, so it reads on a pale deck too. */
const TREMOR_DUST = 0xb9a385;
const _tremor = { mag: 0, puffClock: 0, joltClock: 0.5 };
const _tremorFeet = new THREE.Vector3();

/** Magnitude 0..1 from the mutation; 0 stops it. */
export function setTremor(magnitude) {
  const m = Math.max(0, Math.min(1, magnitude ?? 0));
  if (m > 0 && _tremor.mag === 0) {
    _tremor.puffClock = 0;
    _tremor.joltClock = 0.5;
  }
  _tremor.mag = m;
}

export function tremorState() {
  return _tremor;
}

function tremorFeet() {
  const p = localPlayer.renderPos;
  return _tremorFeet.set(p.x, p.y - PLAYER_HALF_HEIGHT + 0.05, p.z);
}

/** The arrival: one hard kick and a ring of dust thrown up around the player. */
export function tremorJolt(magnitude = 0.5) {
  const m = Math.max(0, Math.min(1, magnitude));
  triggerCameraShake(0.3 + 0.4 * m, 300);
  spawnParticles(tremorFeet(), TREMOR_DUST, 16, 2.4, {
    emitRadius: 4.5, upBias: 0.5, size: 0.6, opacity: 0.6, lifetime: 850, gravity: 3, drag: 0.04,
  });
}

function updateTremor(dt) {
  const m = _tremor.mag;
  const t = performance.now() / 1000;
  const o = cameraShake.offset;
  o.x += (Math.sin(t * 23.0) * 0.09 + Math.sin(t * 5.3) * 0.06) * m;
  o.y += Math.sin(t * 17.0 + 1.1) * 0.05 * m;
  o.z += Math.cos(t * 19.0 + 0.5) * 0.08 * m;
  if (dt <= 0) return;
  // A puff of deck dust every 0.2–0.42 s, faster the harder it shakes.
  _tremor.puffClock += dt;
  const every = 0.42 - 0.22 * m;
  if (_tremor.puffClock >= every) {
    _tremor.puffClock -= every;
    const feet = tremorFeet();
    const a = Math.random() * Math.PI * 2;
    const r = 1 + Math.random() * 4.5;
    feet.x += Math.sin(a) * r;
    feet.z += Math.cos(a) * r;
    spawnParticles(feet, TREMOR_DUST, 6, 1.6, { upBias: 0.8, size: 0.8, opacity: 0.7, lifetime: 800, gravity: 2.5 });
  }
  // And a jolt now and then, so it never settles into a hum.
  _tremor.joltClock -= dt;
  if (_tremor.joltClock <= 0) {
    _tremor.joltClock = 0.7 + Math.random() * 0.9;
    triggerCameraShake(0.06 + 0.16 * m, 120);
  }
}

function createOverlay(zIndex) {
  const el = document.createElement('div');
  el.style.cssText = `position:fixed;inset:0;pointer-events:none;z-index:${zIndex};opacity:0;transition:opacity 0.3s;`;
  document.body.appendChild(el);
  return el;
}

let screenFlashEl = null;
let vignetteEl = null;
let directionalEl = null;

export function screenFlash(color, duration = 300, strength = 0.4) {
  if (!screenFlashEl) screenFlashEl = createOverlay(40);
  screenFlashEl.style.background = color;
  screenFlashEl.style.transition = 'none';
  screenFlashEl.style.opacity = String(strength);
  requestAnimationFrame(() => {
    screenFlashEl.style.transition = `opacity ${duration}ms`;
    screenFlashEl.style.opacity = '0';
  });
}

export function showVignette(color, duration = 2000, strength = 0.6) {
  if (!vignetteEl) vignetteEl = createOverlay(39);
  vignetteEl.style.background = `radial-gradient(ellipse at center, transparent 45%, ${color} 100%)`;
  vignetteEl.style.opacity = String(strength);
  clearTimeout(vignetteEl._timer);
  vignetteEl._timer = setTimeout(() => {
    vignetteEl.style.opacity = '0';
  }, duration);
}

/**
 * Damage vignette biased toward where the hit came from.
 *
 * `angle` is radians in screen space: 0 = ahead, PI/2 = to the right. Being able
 * to turn toward the shooter is the whole reason artillery is playable.
 */
export function showDirectionalDamage(angle, strength = 0.55, duration = 650) {
  if (!directionalEl) directionalEl = createOverlay(38);
  const x = 50 + Math.sin(angle) * 50;
  const y = 50 - Math.cos(angle) * 42;
  directionalEl.style.background =
    `radial-gradient(circle at ${x}% ${y}%, rgba(255,40,70,0.85) 0%, rgba(255,40,70,0.35) 22%, transparent 55%)`;
  directionalEl.style.transition = 'none';
  directionalEl.style.opacity = String(strength);
  requestAnimationFrame(() => {
    directionalEl.style.transition = `opacity ${duration}ms`;
    directionalEl.style.opacity = '0';
  });
}

/** Creeping desaturation at low HP. Set `on` false to clear it. */
let lowHpEl = null;
export function setLowHealthOverlay(on) {
  if (!lowHpEl) {
    lowHpEl = createOverlay(37);
    lowHpEl.style.background =
      'radial-gradient(ellipse at center, transparent 38%, rgba(120,0,20,0.75) 100%)';
    lowHpEl.style.transition = 'opacity 400ms';
  }
  lowHpEl.style.opacity = on ? '0.75' : '0';
}

// ─── Burst pooling ──────────────────────────────────────────────
//
// A burst used to allocate a fresh PlaneGeometry, Float32Arrays, an
// InstancedBufferAttribute and a SpriteNodeMaterial every call, and dispose the
// lot ~1s later. At 8 casting players that is a steady churn of GPU buffer
// creates/destroys plus a fresh node-material build per burst. Resources are now
// pooled per blend mode with a fixed 64-instance capacity (`mesh.count` limits
// the draw, so a 10-particle burst on a 64-slot buffer draws 10 instances); the
// material is re-aimed with uniforms (see ParticleUtils.js) instead of rebuilt.

/** Instance capacity of a pooled burst. Largest live caller uses 40. */
const MAX_BURST_COUNT = 64;
/** Free resources kept per pool key; beyond this, released bursts dispose. */
const POOL_KEEP = 24;
// Two shapes (soft circle, cel shard) × two blend modes. Four programs total —
// the shard variants exist because the reference art's debris is chunky and
// angular, and a soft round puff cannot be art-directed into that.
const _pools = { normal: [], additive: [], 'shard-normal': [], 'shard-additive': [] };

function createBurstResources(poolKey) {
  const shard = poolKey.startsWith('shard-');
  const blendKey = shard ? poolKey.slice(6) : poolKey;
  const positions = new Float32Array(MAX_BURST_COUNT * 3);
  const velocities = new Float32Array(MAX_BURST_COUNT * 3);
  const posAttr = new THREE.InstancedBufferAttribute(positions, 3);
  posAttr.setUsage(THREE.DynamicDrawUsage);
  const geo = new THREE.PlaneGeometry(1, 1);
  geo.setAttribute('burstPos', posAttr);
  const params = { color: 0xffffff, size: 0.3, opacity: 1, blending: blendKey };
  let material;
  let rotAttr = null;
  if (shard) {
    // Per-instance (rotation, size jitter) — baked at spawn, static in flight.
    rotAttr = new THREE.InstancedBufferAttribute(new Float32Array(MAX_BURST_COUNT * 2), 2);
    geo.setAttribute('burstRot', rotAttr);
    material = createShardMaterial(posAttr, rotAttr, params);
  } else {
    material = createParticleMaterial(posAttr, params);
  }
  const mesh = new THREE.Mesh(geo, material);
  mesh.frustumCulled = false;
  return { mesh, posAttr, rotAttr, velocities, poolKey };
}

/**
 * One pooled resource per blend key, TAKEN OUT of the pool for the arena's
 * warm frames (`ArenaRenderer._warmDraw` draws them once through the real
 * pass so the tremor's first puff of deck dust does not compile live — a
 * 200 ms frame in the gym, where the combat warmup never runs) and handed
 * back by `releaseBurstPrewarm`.
 *
 * Out of the pool, not borrowed from it: the first cut returned `pool[0]`
 * and left it there, so a burst spawned during the warm frames (a tremor
 * puff, a hit) popped the very mesh the arena was about to remove from the
 * scene — a live burst pulled out of the world for its whole life
 * (`burst-prewarm.test.js`).
 */
export function acquireBurstPrewarm() {
  const out = [];
  for (const key of Object.keys(_pools)) {
    const res = _pools[key].pop() ?? createBurstResources(key);
    res.mesh.count = 1;
    out.push(res);
  }
  return out;
}

/** Puts warm-frame resources back (or disposes past the cap), out of the scene. */
export function releaseBurstPrewarm(resources) {
  if (!resources) return;
  for (const res of resources) {
    res.mesh.parent?.remove(res.mesh);
    const pool = _pools[res.poolKey];
    if (pool.length < POOL_KEEP) pool.push(res);
    else {
      res.mesh.geometry.dispose();
      res.mesh.material.dispose();
    }
  }
}

/** Returns a finished burst's resources to the pool (or disposes past the cap). */
function releaseBurst(p) {
  _scene.remove(p.mesh);
  const pool = _pools[p.res.poolKey];
  if (pool.length < POOL_KEEP) {
    pool.push(p.res);
  } else {
    p.mesh.geometry.dispose();
    p.mesh.material.dispose();
  }
}

function evictOneBurst() {
  // Prefer a burst that is at least 60% done — deleting the tail of a fading
  // puff is invisible; deleting a just-fired ultimate is not.
  let index = -1;
  let bestAge = -1;
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const age = p.age / p.lifetime;
    if (age >= 0.6 && age > bestAge) {
      bestAge = age;
      index = i;
    }
  }
  if (index < 0) index = 0; // everything is fresh: fall back to oldest
  const [dead] = particles.splice(index, 1);
  releaseBurst(dead);
}

/**
 * A burst of particles.
 *
 * `opts.gravity` in units/s^2 (default 9.8), `opts.dir` gives the burst a
 * direction (used by directional impacts), `opts.upBias` scales the upward
 * component of the launch cone — vy is uniform in [0, speed * upBias], so 0.3
 * gives a low puff that rises and settles (landing dust), 2 a fountain
 * (fault line), and the default 1 the standard omnidirectional pop.
 *
 * `opts.shape: 'shard'` swaps the soft circle for the cel-banded kite.
 * `opts.emitRadius` spawns on a ground circle of that radius instead of at a
 * point, and `opts.inward` aims each particle back at the centre — together
 * they are the ANTICIPATION gather: energy converging on a caster is the
 * universal "something is about to happen" beat.
 */
export function spawnParticles(position, color, count = 20, speed = 5, opts = {}) {
  if (!_scene) return null;
  const budget = getParticleBudget();
  while (particles.length >= budget) evictOneBurst();

  const n = Math.min(count, MAX_BURST_COUNT);
  const blendKey = opts.blending === 'additive' ? 'additive' : 'normal';
  const poolKey = opts.shape === 'shard' ? `shard-${blendKey}` : blendKey;
  const res = _pools[poolKey].pop() ?? createBurstResources(poolKey);
  const positions = res.posAttr.array;
  const velocities = res.velocities;

  const px = position.x ?? position[0] ?? 0;
  const py = position.y ?? position[1] ?? 0;
  const pz = position.z ?? position[2] ?? 0;

  const dir = opts.dir ?? null;
  const upBias = opts.upBias ?? 1;
  const emitRadius = opts.emitRadius ?? 0;

  for (let i = 0; i < n; i++) {
    const i3 = i * 3;
    let vx = (Math.random() - 0.5) * speed;
    let vy = Math.random() * speed * upBias;
    let vz = (Math.random() - 0.5) * speed;
    if (emitRadius > 0) {
      const a = Math.random() * Math.PI * 2;
      const r = emitRadius * (0.85 + Math.random() * 0.15);
      positions[i3] = px + Math.sin(a) * r;
      positions[i3 + 1] = py;
      positions[i3 + 2] = pz + Math.cos(a) * r;
      if (opts.inward) {
        // Converge on the centre; the caller sizes `speed` so arrival lands
        // roughly on the strike beat.
        vx = -Math.sin(a) * speed + vx * 0.15;
        vy = vy * 0.25;
        vz = -Math.cos(a) * speed + vz * 0.15;
      } else {
        vx = Math.sin(a) * speed * 0.8 + vx * 0.3;
        vz = Math.cos(a) * speed * 0.8 + vz * 0.3;
      }
    } else {
      positions[i3] = px;
      positions[i3 + 1] = py;
      positions[i3 + 2] = pz;
    }
    if (dir) {
      vx += dir.x * speed;
      vy += dir.y * speed;
      vz += dir.z * speed;
    }
    velocities[i3] = vx;
    velocities[i3 + 1] = vy;
    velocities[i3 + 2] = vz;
  }
  res.posAttr.needsUpdate = true;
  if (res.rotAttr) {
    const rot = res.rotAttr.array;
    for (let i = 0; i < n; i++) {
      rot[i * 2] = Math.random() * Math.PI * 2;
      rot[i * 2 + 1] = 0.55 + Math.random() * 0.9;
    }
    res.rotAttr.needsUpdate = true;
  }

  setParticleParams(res.mesh.material, {
    color,
    size: opts.size ?? 0.3,
    opacity: opts.opacity ?? 1.0,
  });

  res.mesh.count = n;
  _scene.add(res.mesh);

  const burst = {
    mesh: res.mesh,
    posAttr: res.posAttr,
    velocities,
    res,
    count: n,
    /** ms lived, advanced by the presentation delta — frozen during hitstop. */
    age: 0,
    lifetime: opts.lifetime ?? 1500,
    gravity: opts.gravity ?? 9.8,
    drag: opts.drag ?? 0,
  };
  particles.push(burst);
  return burst;
}

export function updateParticles(dt) {
  // dt = 0 is hitstop: nothing moves and nothing ages. The old code fell back
  // to 16ms here AND aged on Date.now(), so a frozen burst kept fading while it
  // stood still — the one frame the freeze is meant to hold.
  if (!Number.isFinite(dt) || dt < 0) dt = 0;

  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.age += dt * 1000;
    const elapsed = p.age;

    if (elapsed >= p.lifetime) {
      releaseBurst(p);
      particles.splice(i, 1);
      continue;
    }

    if (dt === 0) continue;
    const positions = p.posAttr.array;
    // The pooled backing array is MAX_BURST_COUNT slots; only the live ones move.
    const count = p.count ?? positions.length / 3;
    const vel = p.velocities;
    const dragFactor = p.drag > 0 ? Math.pow(1 - p.drag, dt * 60) : 1;

    for (let j = 0; j < count; j++) {
      const j3 = j * 3;
      positions[j3] += vel[j3] * dt;
      positions[j3 + 1] += vel[j3 + 1] * dt;
      positions[j3 + 2] += vel[j3 + 2] * dt;
      vel[j3 + 1] -= p.gravity * dt;
      if (dragFactor !== 1) {
        vel[j3] *= dragFactor;
        vel[j3 + 1] *= dragFactor;
        vel[j3 + 2] *= dragFactor;
      }
    }

    p.posAttr.needsUpdate = true;
    setParticleFade(p.mesh.material, 1 - elapsed / p.lifetime);
  }
}

export function clearAllParticles() {
  for (const p of particles) releaseBurst(p);
  particles.length = 0;
}
